// Una toma de un producto, como un cuadro del tamaño de la foto con el título DEBAJO
// (ADR-2026-10-14-el-catalogo-arranca-con-el-agente-a-pantalla-completa). Con foto, el cuadro es
// la foto: click → ampliada en el visor, el lápiz → el panel del prompt, y lo pendiente (edición,
// rehacer, generando) en una etiqueta arriba con su X (ADR-2026-10-14-el-agente-edita-el-catalogo-
// en-masa). Generando, el recuadro de carga de toda la app (`RecuadroGenerando`). Con el prompt
// escrito y sin foto, una tarjeta de TEXTO con el prompt adentro (otra forma que la de una foto:
// todavía no hay imagen). Sin prompt, el ícono de la toma y su estado. Click → el panel del prompt.
import { AlertCircle, Loader2, Pencil, RefreshCw, Wand2, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import CachedImg from '../../../../components/CachedImg';
import { RecuadroGenerando } from '../../components/PanelTandaCreaciones';
import { ICONO_DE_TOMA } from './iconosDeToma';
import type { ItemDelPlan, TomaDelPlan } from './plan';

const ESTADO_TEXTO: Record<ItemDelPlan['estado'], string> = {
  'sin-prompt': 'Sin prompt',
  escrito: 'Prompt listo',
  generando: 'Generando…',
  generada: 'Generada',
  fallo: 'No salió',
};

/** El ancho de cada cuadro; el alto sale del formato del catálogo. */
// En el teléfono, dos por fila a lo ancho; desde `sm`, los 168 px de siempre
// (ADR-2026-10-15-el-catalogo-en-el-telefono).
export const ANCHO_TARJETA = 'w-full sm:w-[168px]';

interface Etiqueta {
  icono: ReactNode;
  texto: string;
  quitable?: boolean;
  alerta?: boolean;
}

export const TarjetaDeToma: React.FC<{
  toma: TomaDelPlan;
  item: ItemDelPlan;
  miniatura?: string | null;
  activa: boolean;
  /** La proporción del formato del catálogo, como CSS ("4 / 5"). */
  aspecto: string;
  /** El escritor está trabajando: un ítem sin prompt muestra que se está escribiendo. */
  escribiendo?: boolean;
  onAbrir: () => void;
  /** Con la foto ya salida: verla ampliada (sin esto, el click abre el panel del ítem). */
  onVer?: () => void;
  /** Saca la edición o el rehacer pendiente. */
  onQuitarPendiente?: () => void;
}> = ({ toma, item, miniatura, activa, aspecto, escribiendo = false, onAbrir, onVer, onQuitarPendiente }) => {
  const Icono = ICONO_DE_TOMA[toma.icono];
  const estado = item.estado;
  const titulo = `${toma.titulo}: ${ESTADO_TEXTO[estado]}${item.fijado ? ' (fijado)' : ''}${item.motivo ? ` — ${item.motivo}` : ''}`;
  const escribiendoEste = escribiendo && !item.fijado && !item.prompt.trim() && estado !== 'fallo';
  const ver = miniatura ? onVer : undefined;
  const anillo = activa ? 'ring-2 ring-brand-primary' : 'ring-1 ring-black/[0.06]';
  const [a, b] = aspecto.split('/').map((x) => Number(x.trim()));
  const ratio = a > 0 && b > 0 ? a / b : 1;
  // Sin la hora de lanzamiento (un plan viejo), la barra arranca desde que se ve.
  const [desde] = useState(() => item.lanzadoEn ?? Date.now());
  const generando = estado === 'generando';

  const etiqueta: Etiqueta | null = !miniatura || generando
    ? null
    : item.edicion
        ? { icono: <Wand2 className="h-3 w-3" strokeWidth={2.25} />, texto: `Editar: ${item.edicion.instruccion}`, quitable: true }
        : item.rehacer
          ? { icono: <RefreshCw className="h-3 w-3" strokeWidth={2.25} />, texto: 'Por rehacer', quitable: true }
          : estado === 'escrito'
            ? { icono: <RefreshCw className="h-3 w-3" strokeWidth={2.25} />, texto: 'Prompt nuevo: por rehacer' }
            : estado === 'sin-prompt'
              ? escribiendoEste
                ? { icono: <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />, texto: 'Escribiendo…' }
                : { icono: <span className="block h-1.5 w-1.5 rounded-full bg-current" />, texto: 'Sin prompt' }
              : estado === 'fallo' || item.motivo
                ? { icono: <AlertCircle className="h-3 w-3" strokeWidth={2.25} />, texto: estado === 'fallo' ? 'No salió' : 'Quedó la anterior', alerta: true }
                : null;

  return (
    <figure className={`animate-fade-in group flex shrink-0 flex-col gap-2 ${ANCHO_TARJETA}`}>
      <div className={`relative w-full overflow-hidden rounded-xl transition-[box-shadow,transform] hover:-translate-y-px hover:shadow-md ${anillo}`} style={{ aspectRatio: aspecto }}>
        {generando ? (
          <button type="button" onClick={onAbrir} title={titulo} aria-label={titulo} className="block h-full w-full text-left">
            <RecuadroGenerando ratio={ratio} creadaEn={item.lanzadoEn ?? desde} />
          </button>
        ) : miniatura ? (
          <button type="button" onClick={ver ?? onAbrir} title={titulo} aria-label={ver ? `Ver ${toma.titulo} ampliada` : titulo} className="block h-full w-full bg-black/[0.04]">
            <CachedImg src={miniatura} alt="" className="h-full w-full object-cover" />
          </button>
        ) : estado === 'escrito' && item.prompt.trim() ? (
          // El prompt escrito, sin foto todavía: una hoja de texto, no un hueco de imagen.
          <button
            type="button"
            onClick={onAbrir}
            title={titulo}
            aria-label={`Ver y editar el prompt de ${toma.titulo}`}
            className="flex h-full w-full flex-col gap-2 bg-white p-3 text-left"
          >
            <span className="inline-flex w-fit items-center gap-1 rounded-md bg-[#E9EDFD] py-0.5 px-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-brand-primary">
              {item.fijado ? <Pencil className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" /> : <Wand2 className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />}
              {toma.infografia ? 'Infografía' : item.fijado ? 'Prompt editado' : 'Prompt'}
            </span>
            <span className="relative min-h-0 flex-1 overflow-hidden text-[12px] leading-[1.45] text-[#3C3C43]/75">
              {item.prompt.trim()}
              <span className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent" aria-hidden="true" />
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={onAbrir}
            title={titulo}
            aria-label={titulo}
            className={`flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center ${estado === 'fallo' ? 'bg-[#FFF5F4]' : 'bg-[#F5F6FA]'}`}
          >
            <span className="grid h-10 w-10 place-items-center rounded-full bg-white text-[#02062B]/[0.65] shadow-sm">
              <Icono className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
            </span>
            <span className={`inline-flex items-center gap-1 text-[12px] font-semibold ${estado === 'fallo' ? 'text-[#B42318]' : 'text-[#3C3C43]/60'}`}>
              {escribiendoEste ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-primary" strokeWidth={2.25} aria-hidden="true" />
              ) : estado === 'fallo' ? (
                <AlertCircle className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
              ) : null}
              {escribiendoEste ? 'Escribiendo…' : ESTADO_TEXTO[estado]}
            </span>
          </button>
        )}
        {etiqueta && (
          <span
            title={item.motivo ?? etiqueta.texto}
            className={`absolute left-1.5 top-1.5 flex max-w-[calc(100%-2.75rem)] items-center gap-1 rounded-md py-0.5 pl-1.5 text-[11.5px] font-semibold shadow-sm ${
              etiqueta.quitable && onQuitarPendiente ? 'pr-0.5' : 'pr-1.5'
            } ${etiqueta.alerta ? 'bg-[#FFF5F4] text-[#B42318]' : 'bg-white/95 text-brand-black'}`}
          >
            <span className="shrink-0" aria-hidden="true">
              {etiqueta.icono}
            </span>
            <span className="truncate">{etiqueta.texto}</span>
            {etiqueta.quitable && onQuitarPendiente && (
              <button
                type="button"
                onClick={onQuitarPendiente}
                aria-label={`No ${item.edicion ? 'editar' : 'rehacer'} ${toma.titulo}`}
                title="Sacar"
                className="grid h-4 w-4 shrink-0 place-items-center rounded text-[#02062B]/[0.5] hover:bg-black/[0.06] hover:text-brand-black"
              >
                <X className="h-3 w-3" strokeWidth={2.5} />
              </button>
            )}
          </span>
        )}
        {ver && !generando && (
          <button
            type="button"
            onClick={onAbrir}
            title="Ver y editar el prompt"
            aria-label={`Ver y editar el prompt de ${toma.titulo}`}
            className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-md bg-white/90 text-brand-black opacity-0 shadow-sm transition-opacity hover:bg-white focus-visible:opacity-100 group-hover:opacity-100"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
          </button>
        )}
      </div>
      <figcaption className="flex min-w-0 items-center gap-1.5 px-0.5 text-[13px] font-semibold text-brand-black">
        <Icono className="h-3.5 w-3.5 shrink-0 text-[#02062B]/[0.55]" strokeWidth={2} aria-hidden="true" />
        <span className="truncate" title={toma.titulo}>
          {toma.titulo}
        </span>
      </figcaption>
    </figure>
  );
};
